import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
    Box,
    Typography,
    Button,
    Chip,
    Avatar,
    IconButton,
    LinearProgress,
    Card,
    CardContent,
} from '@mui/material';
import {
    Close as CloseIcon,
    EmojiEvents as TrophyIcon,
    CloudUpload as UploadIcon,
    Delete as DeleteIcon,
    Feedback as FeedbackIcon,
    Image as ImageIcon,
    VideoLibrary as VideoIcon,
    CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { ISubmission, IProject } from '../../../types/global/helper';
import CustomLazyLoadImage from '../../Image/CustomLazyLoadImage';
import { DialogueSheet } from '../../DialogueSheet/DialogueSheet';
import { MixPanel, MIXPANEL_EVENT } from '../../../analytics/Mixpanel';
import { updateSubmission } from '../../../redux/contest/actions';
import { createProject } from '../../../redux/project/actions';
import { isEmpty } from '../../../utils/lodash';
import { IAwardPrize } from '../../../redux/contest/types';
import { useDispatchPromise, showToastMessage } from '../../../utils/AppUtils';
import { generateVideoSnapshot } from '../../VideoPlayer/VideoSnapshotGenerator';
import { base64ToFileWithDetails } from '../../../utils/mediaHelper';
import { ICreateProject } from '../../../redux/project/types';
import { RichTextViewer } from '../../Common/RichTextViewer';

export interface IContestSubmissionModalProps {
    open: boolean;
    onClose: () => void;
    submission: ISubmission;
    onRescindSubmission?: (submission: ISubmission) => void;
}

export const ContestSubmissionModal: React.FC<IContestSubmissionModalProps> = ({
    open,
    onClose,
    submission,
    onRescindSubmission,
}) => {
    const dispatch = useDispatchPromise();
    const { themeStyle } = useThemeContext();
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [uploadingVideo, setUploadingVideo] = useState(false);
    const [showWithdrawConfirm, setShowWithdrawConfirm] = useState(false);
    const [selectedSubmission, setSelectedSubmission] = useState(submission);
    const [videoThumbnail, setVideoThumbnail] = useState<string | null>(null);

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setUploadingPhoto(true);
            // Call updateSubmission with the file - the saga will handle the upload
            dispatch(
                updateSubmission({
                    submissionId: selectedSubmission._id,
                    submission_picture: file,
                }),
            )
                .then((updatedSub: any) => {
                    setSelectedSubmission(prevState => ({
                        ...prevState,
                        submission_picture: updatedSub.submission_picture,
                    }));
                    setUploadingPhoto(false);
                })
                .catch(e => {
                    setUploadingPhoto(false);
                });
        }
    };

    const handleVideoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setUploadingVideo(true);

            // Create video element to extract metadata
            const videoElement = document.createElement('video');
            videoElement.preload = 'metadata';
            videoElement.src = URL.createObjectURL(file);

            // Set a timeout for video metadata loading
            let videoTimeout = setTimeout(() => {
                setUploadingVideo(false);
                showToastMessage(
                    'Video processing took too long. Try a different file or ensure device has enough memory.',
                    'error',
                );
            }, 20000);

            videoElement.addEventListener('loadedmetadata', async () => {
                clearTimeout(videoTimeout);

                try {
                    // Extract video metadata
                    const videoMetadata = {
                        duration: videoElement.duration,
                        width: videoElement.videoWidth,
                        height: videoElement.videoHeight,
                        type: file.type,
                        codec: '',
                    };

                    // Generate thumbnail for the video
                    const thumbnail = await generateVideoSnapshot(videoElement.src, 2); // Capture at 2 seconds
                    setVideoThumbnail(thumbnail);
                    const image = base64ToFileWithDetails(thumbnail).file;
                    // Create a project with the video file and metadata
                    const projectPayload: ICreateProject = {
                        title: `Testimonial Video - ${selectedSubmission.project.title}`,
                        description: 'Testimonial video for award submission',
                        project_type: 'short',
                        media_type: 'video',
                        visible: false, // Keep it private
                        public_status: 'unlisted',
                        project_file: file,
                        feature_image: image,
                        project: '',
                        metadata: videoMetadata,
                    };

                    dispatch(createProject(projectPayload))
                        .then((createdProject: any) => {
                            // Update submission with the created project
                            return dispatch(
                                updateSubmission({
                                    submissionId: selectedSubmission._id,
                                    testimonial_video: createdProject._id,
                                }),
                            );
                        })
                        .then((updatedSub: any) => {
                            setSelectedSubmission(prevState => ({
                                ...prevState,
                                testimonial_video: updatedSub.testimonial_video,
                            }));
                            setUploadingVideo(false);
                        })
                        .catch(e => {
                            console.error('Error creating project or updating submission:', e);
                            setUploadingVideo(false);
                        });
                } catch (error) {
                    console.error('Error processing video:', error);
                    setUploadingVideo(false);
                } finally {
                    URL.revokeObjectURL(videoElement.src); // Clean up the object URL
                }
            });

            // Add error handler for video element
            videoElement.addEventListener('error', () => {
                clearTimeout(videoTimeout);
                setUploadingVideo(false);
                showToastMessage('Error loading video. Please try a different file.', 'error');
                URL.revokeObjectURL(videoElement.src);
            });
        }
    };

    const handleWithdraw = () => {
        if (onRescindSubmission) {
            onRescindSubmission(selectedSubmission);
        }
        setShowWithdrawConfirm(false);
        onClose();
    };

    useEffect(() => {
        setSelectedSubmission(submission);

        // Set thumbnail for existing video project
        if (submission.testimonial_video && typeof submission.testimonial_video === 'object') {
            const videoProject = submission.testimonial_video as IProject;
            setVideoThumbnail(videoProject.thumbnail || videoProject.feature_image);
        }
    }, [submission]);

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'winner':
                return 'success';
            case 'semi-finalist':
            case 'finalist':
                return 'warning';
            case 'not-selected':
                return 'error';
            default:
                return 'default';
        }
    };

    const getStatusIcon = (status: string) => {
        if (status === 'winner' || status === 'semi-finalist' || status === 'finalist') {
            return <TrophyIcon />;
        }
        return <TrophyIcon />;
    };

    const formatStatus = (awards?: IAwardPrize[]) => {
        if (!awards || awards.length === 0) return '';

        return awards
            .map(a => a.award_category?.category_title) // safely pick award_category.type
            .filter((t): t is string => Boolean(t)) // remove undefined/null
            .join(' & '); // join with " & "
    };

    // Get judge feedback from jury scores
    const judgeFeedbacks = selectedSubmission.jury_notes || [];
    const hasWonAward =
        ['winner', 'semi-finalist', 'finalist', 'second', 'third', 'fourth'].includes(
            selectedSubmission.judging_status,
        ) ||
        (selectedSubmission.awards && !isEmpty(selectedSubmission.awards));

    // Check completion status
    const hasUploadedPhoto = !!selectedSubmission.submission_picture;
    const hasUploadedVideo = !!(selectedSubmission.testimonial_video as IProject)?._id;
    const isFullyCompleted = hasUploadedPhoto && hasUploadedVideo;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            fullScreen
            PaperProps={{
                sx: {
                    bgcolor: theme[themeStyle].deep_grey_lighter,
                    color: theme[themeStyle].tag_white,
                    borderRadius: { xs: 0, sm: 2 },
                    border: { xs: 'none', sm: `1px solid ${theme[themeStyle].darkGray60}` },
                    margin: { xs: 0, sm: 'auto' },
                    maxHeight: { xs: '100vh', sm: '90vh' },
                    height: { xs: '100vh', sm: 'auto' },
                },
            }}
            sx={{
                '& .MuiDialog-paper': {
                    width: { xs: '100vw', sm: '50vw' },
                    height: { xs: '100vh', sm: '80vh' },
                },
            }}>
            <Box sx={{ p: 3, pb: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                        <DialogTitle
                            sx={{ p: 0, fontSize: '1.5rem', fontWeight: 600, color: theme[themeStyle].tag_white }}>
                            {selectedSubmission.project.title}
                        </DialogTitle>
                        <Typography variant="body2" sx={{ color: theme[themeStyle].tag_white, mt: 0.5 }}>
                            {selectedSubmission.contest.title} • {selectedSubmission.category}
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                            label={formatStatus(selectedSubmission.awards)}
                            color={getStatusColor(selectedSubmission.judging_status) as any}
                            icon={getStatusIcon(selectedSubmission.judging_status)}
                            sx={{
                                fontWeight: 600,
                                '& .MuiChip-label': { px: 1.5 },
                                backgroundColor: theme[themeStyle].highlight,
                                color: theme[themeStyle].black,
                            }}
                        />
                        <IconButton onClick={onClose} sx={{ color: theme[themeStyle].tag_white }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>
            </Box>

            <DialogContent sx={{ p: 0 }}>
                {/* Full-width contest image */}
                <Box sx={{ width: '100%', mb: 3, maxHeight: '300px', overflow: 'hidden' }}>
                    <CustomLazyLoadImage
                        src={selectedSubmission.contest.feature_image}
                        alt="Contest Feature"
                        width="100%"
                        height="300px"
                        style={{
                            objectFit: 'cover',
                            width: '100%',
                        }}
                    />
                </Box>

                <Box sx={{ p: 3, pt: 0 }}>
                    <Box display="flex" flexDirection="column" gap={3}>
                        {/* Submission Details */}
                        <Card
                            sx={{
                                bgcolor: theme[themeStyle].deep_grey,
                                border: `1px solid ${theme[themeStyle].darkGray60}`,
                            }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        mb: 2,
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.REGULAR_HEADER,
                                    }}>
                                    Submission Details
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        mb: 2,
                                        color: theme[themeStyle].tag_white,
                                        lineHeight: 1.6,
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    }}>
                                    {selectedSubmission.project.description}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ color: theme[themeStyle].tag_white, fontFamily: FONT_ENUM.MAIN_REGULAR }}>
                                    Submitted on{' '}
                                    {new Date(selectedSubmission.createdAt).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                    })}
                                </Typography>
                            </CardContent>
                        </Card>

                        {/* Award Information */}
                        {hasWonAward && (
                            <Card
                                sx={{
                                    bgcolor: theme[themeStyle].deep_grey,
                                    border: `1px solid ${theme[themeStyle].highlight}`,
                                }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                                        <Avatar sx={{ bgcolor: theme[themeStyle].highlight, width: 40, height: 40 }}>
                                            <TrophyIcon sx={{ color: theme[themeStyle].black }} />
                                        </Avatar>
                                        <Box>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    color: theme[themeStyle].tag_white,
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                }}>
                                                🎉 Congratulations!
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: theme[themeStyle].tag_white,
                                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                }}>
                                                You won {formatStatus(selectedSubmission.awards)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            mb: 1,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: 12,
                                        }}>
                                        Your submission has been recognized for excellence in this campaign.
                                    </Typography>
                                </CardContent>
                            </Card>
                        )}

                        {/* Award Photo Upload */}
                        {hasWonAward && (
                            <Card
                                sx={{
                                    bgcolor: theme[themeStyle].deep_grey,
                                    border: `1px solid ${theme[themeStyle].darkGray60}`,
                                }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            mb: 2,
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                        }}>
                                        Award Photo
                                    </Typography>
                                    {selectedSubmission.submission_picture ? (
                                        <Box>
                                            <Box
                                                component="img"
                                                src={selectedSubmission.submission_picture}
                                                alt="Award photo"
                                                sx={{
                                                    width: '100%',
                                                    maxHeight: 200,
                                                    objectFit: 'cover',
                                                    borderRadius: 1,
                                                    mb: 2,
                                                }}
                                            />
                                            <Button
                                                variant="outlined"
                                                component="label"
                                                startIcon={<UploadIcon />}
                                                sx={{
                                                    color: theme[themeStyle].highlight,
                                                    borderColor: theme[themeStyle].highlight,
                                                    '&:hover': {
                                                        borderColor: theme[themeStyle].highlight,
                                                        bgcolor: `${theme[themeStyle].highlight}20`,
                                                    },
                                                }}>
                                                Replace Photo
                                                <input
                                                    type="file"
                                                    hidden
                                                    accept="image/*"
                                                    onChange={handleFileUpload}
                                                />
                                            </Button>
                                        </Box>
                                    ) : (
                                        <Box>
                                            <Box
                                                sx={{
                                                    border: `2px dashed ${theme[themeStyle].darkGray60}`,
                                                    borderRadius: 1,
                                                    p: 4,
                                                    textAlign: 'center',
                                                    mb: 2,
                                                }}>
                                                <ImageIcon
                                                    sx={{ fontSize: 48, color: theme[themeStyle].tag_white, mb: 1 }}
                                                />
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: theme[themeStyle].tag_white,
                                                        mb: 2,
                                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                    }}>
                                                    Upload a photo of yourself for social promotion purposes
                                                </Typography>
                                                <Button
                                                    variant="contained"
                                                    component="label"
                                                    startIcon={<UploadIcon />}
                                                    disabled={uploadingPhoto}
                                                    sx={{
                                                        bgcolor: theme[themeStyle].highlight,
                                                        color: theme[themeStyle].black,
                                                        '&:hover': { bgcolor: theme[themeStyle].highlight },
                                                    }}>
                                                    {uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                                                    <input
                                                        type="file"
                                                        hidden
                                                        accept="image/*"
                                                        onChange={handleFileUpload}
                                                    />
                                                </Button>
                                            </Box>
                                            {uploadingPhoto && <LinearProgress sx={{ mb: 2 }} />}
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        )}

                        {/* Testimonial Video Upload */}
                        {hasWonAward && (
                            <Card
                                sx={{
                                    bgcolor: theme[themeStyle].deep_grey,
                                    border: `1px solid ${theme[themeStyle].darkGray60}`,
                                }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            mb: 2,
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                        }}>
                                        Testimonial Video
                                    </Typography>

                                    {/* Promotional Rights Notification */}
                                    <Box
                                        sx={{
                                            mb: 2,
                                            p: 2,
                                            bgcolor: `${theme[themeStyle].highlight}20`,
                                            border: `1px solid ${theme[themeStyle].highlight}`,
                                            borderRadius: 1,
                                        }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                fontSize: '12px',
                                                lineHeight: 1.4,
                                            }}>
                                            📢{' '}
                                            <strong>
                                                By uploading, you agree to grant Thspian the right to use this material
                                                for promotional purposes across social platforms.
                                            </strong>
                                        </Typography>
                                    </Box>
                                    {selectedSubmission.testimonial_video ? (
                                        <Box>
                                            <Box
                                                onClick={() => {
                                                    const videoProject =
                                                        selectedSubmission.testimonial_video as IProject;
                                                    if (videoProject?.project) {
                                                        window.open(videoProject.project, '_blank');
                                                    }
                                                }}
                                                sx={{
                                                    position: 'relative',
                                                    width: '100%',
                                                    maxHeight: 200,
                                                    borderRadius: 1,
                                                    mb: 2,
                                                    overflow: 'hidden',
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        '& .play-overlay': {
                                                            opacity: 1,
                                                        },
                                                    },
                                                }}>
                                                {/* Video Thumbnail */}
                                                <CustomLazyLoadImage
                                                    src={
                                                        videoThumbnail ||
                                                        (selectedSubmission.testimonial_video as IProject)?.thumbnail ||
                                                        (selectedSubmission.testimonial_video as IProject)
                                                            ?.feature_image
                                                    }
                                                    alt="Testimonial video thumbnail"
                                                    style={{
                                                        width: '100%',
                                                        height: '200px',
                                                        objectFit: 'cover',
                                                    }}
                                                />

                                                {/* Play Button Overlay */}
                                                <Box
                                                    className="play-overlay"
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        right: 0,
                                                        bottom: 0,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                        opacity: 0,
                                                        transition: 'opacity 0.3s ease',
                                                    }}>
                                                    <Box
                                                        sx={{
                                                            width: 60,
                                                            height: 60,
                                                            borderRadius: '50%',
                                                            backgroundColor: theme[themeStyle].highlight,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            color: theme[themeStyle].black,
                                                        }}>
                                                        <VideoIcon sx={{ fontSize: 32 }} />
                                                    </Box>
                                                </Box>
                                            </Box>
                                            <Button
                                                variant="outlined"
                                                component="label"
                                                startIcon={<UploadIcon />}
                                                sx={{
                                                    color: theme[themeStyle].highlight,
                                                    borderColor: theme[themeStyle].highlight,
                                                    '&:hover': {
                                                        borderColor: theme[themeStyle].highlight,
                                                        bgcolor: `${theme[themeStyle].highlight}20`,
                                                    },
                                                }}>
                                                Replace Video
                                                <input
                                                    type="file"
                                                    hidden
                                                    accept="video/*"
                                                    onChange={handleVideoUpload}
                                                />
                                            </Button>
                                        </Box>
                                    ) : (
                                        <Box>
                                            <Box
                                                sx={{
                                                    border: `2px dashed ${theme[themeStyle].darkGray60}`,
                                                    borderRadius: 1,
                                                    p: 4,
                                                    textAlign: 'center',
                                                    mb: 2,
                                                }}>
                                                <VideoIcon
                                                    sx={{ fontSize: 48, color: theme[themeStyle].tag_white, mb: 1 }}
                                                />
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: theme[themeStyle].tag_white,
                                                        mb: 2,
                                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                    }}>
                                                    Upload a testimonial video for social promotion purposes
                                                </Typography>

                                                {/* Promotional Rights Notification in Upload Area */}
                                                <Box
                                                    sx={{
                                                        mb: 2,
                                                        p: 1.5,
                                                        bgcolor: `${theme[themeStyle].highlight}15`,
                                                        border: `1px solid ${theme[themeStyle].highlight}40`,
                                                        borderRadius: 1,
                                                    }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                            fontSize: '11px',
                                                            lineHeight: 1.3,
                                                            textAlign: 'center',
                                                        }}>
                                                        📢 By uploading, you agree to grant Thspian promotional rights
                                                    </Typography>
                                                </Box>
                                                <Button
                                                    variant="contained"
                                                    component="label"
                                                    startIcon={<UploadIcon />}
                                                    disabled={uploadingVideo}
                                                    sx={{
                                                        bgcolor: theme[themeStyle].highlight,
                                                        color: theme[themeStyle].black,
                                                        '&:hover': { bgcolor: theme[themeStyle].highlight },
                                                    }}>
                                                    {uploadingVideo ? 'Uploading...' : 'Upload Video'}
                                                    <input
                                                        type="file"
                                                        hidden
                                                        accept="video/*"
                                                        onChange={handleVideoUpload}
                                                    />
                                                </Button>
                                            </Box>
                                            {uploadingVideo && <LinearProgress sx={{ mb: 2 }} />}
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        )}

                        {/* Completion Status */}
                        {hasWonAward && (
                            <Card
                                sx={{
                                    bgcolor: isFullyCompleted
                                        ? theme[themeStyle].highlight
                                        : theme[themeStyle].deep_grey,
                                    border: `1px solid ${
                                        isFullyCompleted ? theme[themeStyle].highlight : theme[themeStyle].darkGray60
                                    }`,
                                }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                                        <CheckIcon
                                            sx={{
                                                color: isFullyCompleted
                                                    ? theme[themeStyle].black
                                                    : theme[themeStyle].tag_white,
                                                fontSize: 32,
                                            }}
                                        />
                                        <Box>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    color: isFullyCompleted
                                                        ? theme[themeStyle].black
                                                        : theme[themeStyle].tag_white,
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                }}>
                                                {isFullyCompleted ? '🎉 All Set!' : 'Complete Your Profile'}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: isFullyCompleted
                                                        ? theme[themeStyle].black
                                                        : theme[themeStyle].tag_white,
                                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                }}>
                                                {isFullyCompleted
                                                    ? 'Your award profile is complete and ready for promotion!'
                                                    : 'Upload your photo and testimonial video to complete your award profile.'}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Box display="flex" flexDirection="column" gap={1}>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <CheckIcon
                                                sx={{
                                                    color: hasUploadedPhoto
                                                        ? theme[themeStyle].highlight
                                                        : theme[themeStyle].darkGray60,
                                                    fontSize: 20,
                                                }}
                                            />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: hasUploadedPhoto
                                                        ? theme[themeStyle].tag_white
                                                        : theme[themeStyle].darkGray60,
                                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                }}>
                                                {hasUploadedPhoto ? 'Award photo uploaded' : 'Upload award photo'}
                                            </Typography>
                                        </Box>

                                        <Box display="flex" alignItems="center" gap={1}>
                                            <CheckIcon
                                                sx={{
                                                    color: hasUploadedVideo
                                                        ? theme[themeStyle].highlight
                                                        : theme[themeStyle].darkGray60,
                                                    fontSize: 20,
                                                }}
                                            />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: hasUploadedVideo
                                                        ? theme[themeStyle].tag_white
                                                        : theme[themeStyle].darkGray60,
                                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                }}>
                                                {hasUploadedVideo
                                                    ? 'Testimonial video uploaded'
                                                    : 'Upload testimonial video'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        )}

                        {/* Judge Feedback Section */}
                        {judgeFeedbacks.length > 0 && (
                            <Card
                                sx={{
                                    bgcolor: theme[themeStyle].deep_grey,
                                    border: `1px solid ${theme[themeStyle].darkGray60}`,
                                }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                                        <FeedbackIcon sx={{ color: theme[themeStyle].highlight }} />
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.REGULAR_HEADER,
                                            }}>
                                            {judgeFeedbacks.length === 1 ? 'Judge Feedback' : 'Judges’ Feedback'}
                                        </Typography>
                                    </Box>
                                    {judgeFeedbacks.map((feedback: any, index: number) => (
                                        <Box key={index} sx={{ mb: 2 }}>
                                            {judgeFeedbacks.length > 1 && (
                                                <Typography
                                                    sx={{
                                                        color: theme[themeStyle].highlight,
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                        mb: 1,
                                                    }}>
                                                    Judge {index + 1}
                                                </Typography>
                                            )}
                                            <RichTextViewer content={feedback} textStyle={{ padding: 0 }} />
                                        </Box>
                                    ))}
                                </CardContent>
                            </Card>
                        )}

                        {/* Withdrawal Section */}
                        {selectedSubmission.judging_status === 'submitted' &&
                            selectedSubmission.contest.contest_phase !== 'closed' && (
                                <Card
                                    sx={{
                                        bgcolor: theme[themeStyle].deep_grey,
                                        border: `1px solid ${theme[themeStyle].primary_red || '#ef4444'}`,
                                    }}>
                                    <CardContent sx={{ p: 3 }}>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                mb: 2,
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                            }}>
                                            Withdraw Submission
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: theme[themeStyle].tag_white,
                                                mb: 2,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            }}>
                                            Once withdrawn, you can resubmit to this campaign while it is still active.
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            startIcon={<DeleteIcon />}
                                            onClick={() => setShowWithdrawConfirm(true)}
                                            sx={{
                                                borderColor: theme[themeStyle].primary_red || '#ef4444',
                                                color: theme[themeStyle].primary_red || '#ef4444',
                                                '&:hover': {
                                                    borderColor: theme[themeStyle].primary_red || '#ef4444',
                                                    bgcolor: `${theme[themeStyle].primary_red || '#ef4444'}20`,
                                                },
                                            }}>
                                            Withdraw Submission
                                        </Button>
                                    </CardContent>
                                </Card>
                            )}
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 0 }}>
                <Button
                    onClick={onClose}
                    variant="contained"
                    sx={{
                        bgcolor: theme[themeStyle].highlight,
                        color: theme[themeStyle].black,
                        '&:hover': { bgcolor: theme[themeStyle].highlight },
                        px: 4,
                    }}>
                    Close
                </Button>
            </DialogActions>
            <DialogueSheet
                onClose={() => setShowWithdrawConfirm(false)}
                open={showWithdrawConfirm}
                onAgreeClicked={() => {
                    MixPanel.track(MIXPANEL_EVENT.PROCEED_WITH_RESCIND_SUBMISSION, {
                        buttonAction: 'PROCEED_WITH_RESCIND_SUBMISSION',
                        buttonName: 'PROCEED_WITH_RESCIND_SUBMISSION',
                    });
                    handleWithdraw();
                }}
                onDisagreeClick={() => setShowWithdrawConfirm(false)}
                header="Rescind Submission?"
                body="You are about to remove yourself from this campaign. This action cannot be reversed"
            />
        </Dialog>
    );
};
